import type React from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import globalWindow from 'global';
import useAnalyticsFoStageToolBar from '../../../components/stage-toolbar/analytics/useAnalyticsForStageToolBar';
import { CONTROL_TYPES } from '../../../components/stage-toolbar/constants';
import type { DragEventT } from '../../constants';
import type { OnChangeStageSettings, OnCommitStageSettings } from '../types';
type HandleZoomParamsT = {
  clientX: number;
  clientY: number;
  stageRectLeft: number;
  stageRectTop: number;
  stageClientWidth: number;
  stageClientHeight: number;
  scaleFactorToApply: number;
  zoomAction: typeof ZOOM_IN | typeof ZOOM_OUT;
};
type ParamsT = {
  containerRef: React.RefObject<HTMLDivElement>;
  stageRef: React.RefObject<HTMLDivElement>;
  isDraggable: boolean;
  isDrawing: boolean;
  dragEventRef: React.RefObject<DragEventT | null>;
  onChangeStageSettingsAndApplyToDOMNodes: OnChangeStageSettings;
  onCommitStageSettingsAndApplyToDOMNodes: OnCommitStageSettings;
  onShowStageDragHelperText: () => void;
};
const MIN_SCALE = 0.2;
const DEFAULT_MAX_SCALE = 50;
const ZOOM_FLUSH_DELAY = 150;
const ZOOM_IN = 'ZOOM_IN';
const ZOOM_OUT = 'ZOOM_OUT';
const useZoomAndPan = ({
  containerRef,
  stageRef,
  isDraggable,
  isDrawing,
  dragEventRef,
  onChangeStageSettingsAndApplyToDOMNodes,
  onCommitStageSettingsAndApplyToDOMNodes,
  onShowStageDragHelperText
}: ParamsT) => {
  const isMousePressed = useRef<boolean>(false);
  const {
    sendEventChangeControls
  } = useAnalyticsFoStageToolBar();
  const zoomFlushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onShowStageDragHelperTextRef = useRef(onShowStageDragHelperText);
  // eslint-disable-next-line react-hooks/refs
  onShowStageDragHelperTextRef.current = onShowStageDragHelperText;
  const isDraggableRef = useRef<boolean>(isDraggable);
  // eslint-disable-next-line react-hooks/refs
  isDraggableRef.current = isDraggable;
  const isDrawingRef = useRef<boolean>(isDrawing);
  // eslint-disable-next-line react-hooks/refs
  isDrawingRef.current = isDrawing;
  const maxScale = useMemo(() => {
    // eslint-disable-next-line react-hooks/refs
    if (!stageRef.current) {
      return DEFAULT_MAX_SCALE;
    }
    const stageElement = stageRef.current;
    const {
      clientWidth: stageClientWidth
    } = stageElement;
    const deviceResolution = globalWindow.screen.width * globalWindow.devicePixelRatio;
    // eslint-disable-next-line react-hooks/refs
    return deviceResolution / stageClientWidth * 100;
  }, [stageRef]);
  const handleZoom = useCallback(({
    clientX,
    clientY,
    stageRectLeft,
    stageRectTop,
    stageClientWidth,
    stageClientHeight,
    scaleFactorToApply,
    zoomAction
  }: HandleZoomParamsT) => {
    onChangeStageSettingsAndApplyToDOMNodes(prevState => {
      const {
        x: prevOriginX,
        y: prevOriginY,
        scale: prevScale
      } = prevState;
      if (prevScale <= MIN_SCALE && zoomAction === ZOOM_OUT) {
        return prevState;
      } else if (prevScale >= maxScale && zoomAction === ZOOM_IN) {
        return prevState;
      }
      const rawUpdatedScale = prevState.scale * scaleFactorToApply;
      // Clamp the scale between MIN_SCALE and maxScale
      const updatedScale = Math.max(Math.min(rawUpdatedScale, maxScale), MIN_SCALE);

      // Derive pointer origin from the stage's measured rect to keep the zoom centered under the cursor.

      const originX = prevOriginX + (clientX - stageRectLeft) - stageClientWidth / 2 * prevScale;
      const originY = prevOriginY + (clientY - stageRectTop) - stageClientHeight / 2 * prevScale;
      const updatedX = originX - (originX - prevOriginX) * scaleFactorToApply;
      const updatedY = originY - (originY - prevOriginY) * scaleFactorToApply;
      const hasZoomLevelChanged = Math.floor(prevState.scale) !== Math.floor(updatedScale);
      if (hasZoomLevelChanged) {
        sendEventChangeControls({
          action: CONTROL_TYPES.ZOOM,
          value: updatedScale.toString()
        });
      }
      return {
        ...prevState,
        x: updatedX,
        y: updatedY,
        scale: updatedScale
      };
    });
  }, [maxScale, sendEventChangeControls, onChangeStageSettingsAndApplyToDOMNodes]);
  const handleMouseDown = useCallback(() => {
    isMousePressed.current = true;
  }, []);
  const handleMouseUp = useCallback(() => {
    isMousePressed.current = false;
    onCommitStageSettingsAndApplyToDOMNodes();
  }, [onCommitStageSettingsAndApplyToDOMNodes]);
  const handleMouseMove = useCallback((event: MouseEvent) => {
    if (!isMousePressed.current) {
      return;
    }

    // Skip while a vertex/annotation is being dragged (tracked by stage-provider's
    // dragEventRef), not just when the raw mousemove target is off the stage background.
    if (dragEventRef.current) {
      return;
    }

    // Panning is allowed while the mouse is pressed; requires Alt/Option to pan while drawing.
    if (isDrawingRef.current && !event.altKey) {
      return;
    }
    if (!isDraggableRef.current && !event.altKey) {
      onShowStageDragHelperTextRef.current();
      return;
    }
    event.preventDefault();
    onChangeStageSettingsAndApplyToDOMNodes(prevState => ({
      ...prevState,
      x: prevState.x + event.movementX,
      y: prevState.y + event.movementY
    }));
  }, [dragEventRef, onChangeStageSettingsAndApplyToDOMNodes]);
  const handleWheelEvent = useCallback((event: WheelEvent) => {
    if (!stageRef.current) {
      return;
    }
    event.preventDefault();
    const stageElement = stageRef.current;
    const {
      clientWidth: stageClientWidth,
      clientHeight: stageClientHeight
    } = stageElement;
    const stageRect = stageElement.getBoundingClientRect();
    const scaleBy = event.deltaY < 0 ? 1.1 : 1 / 1.1;
    const zoomAction = event.deltaY < 0 ? ZOOM_IN : ZOOM_OUT;
    handleZoom({
      clientX: event.clientX,
      clientY: event.clientY,
      stageRectLeft: stageRect.left,
      stageRectTop: stageRect.top,
      stageClientWidth,
      stageClientHeight,
      scaleFactorToApply: scaleBy,
      zoomAction
    });

    // Debounced flush: reset timer on each wheel event, commit only after
    // ZOOM_FLUSH_DELAY ms of no wheel events. This is done because there's no
    // onMouseUp equivalent for wheel events.
    if (zoomFlushTimerRef.current) {
      clearTimeout(zoomFlushTimerRef.current);
    }
    zoomFlushTimerRef.current = setTimeout(() => {
      onCommitStageSettingsAndApplyToDOMNodes();
      zoomFlushTimerRef.current = null;
    }, ZOOM_FLUSH_DELAY);
  }, [stageRef, handleZoom, onCommitStageSettingsAndApplyToDOMNodes]);
  useEffect(() => {
    if (!containerRef.current) {
      return;
    }
    const containerElement = containerRef.current;
    containerElement.addEventListener('mousedown', handleMouseDown);
    containerElement.addEventListener('mouseup', handleMouseUp);
    containerElement.addEventListener('mousemove', handleMouseMove);
    containerElement.addEventListener('wheel', handleWheelEvent);
    return () => {
      containerElement.removeEventListener('mousedown', handleMouseDown);
      containerElement.removeEventListener('mouseup', handleMouseUp);
      containerElement.removeEventListener('mousemove', handleMouseMove);
      containerElement.removeEventListener('wheel', handleWheelEvent);
    };
  }, [containerRef, handleMouseDown, handleMouseUp, handleMouseMove, handleWheelEvent]);
};
export default useZoomAndPan;